import React, { useEffect, useState } from 'react';

import { secondsToMinutes } from '@femtasy/helpers';
import Hammer from 'react-hammerjs';

import Typography from 'components/Typography';

import { usePlayer } from 'hooks/usePlayer';

const useSeek = () => {
  const { mediaDuration, currentTime, requestSeekToTime } = usePlayer();

  const [progressBarTime, setProgressBarTime] = useState<number>(currentTime);
  const [isSeeking, setIsSeeking] = useState<boolean>(false);

  useEffect(() => {
    if (!isSeeking) {
      setProgressBarTime(currentTime);
    }
  }, [currentTime, isSeeking]);

  const calculateTime = React.useCallback(
    (event: HammerInput) => {
      setIsSeeking(true);

      if (event.srcEvent.type === 'pointercancel') {
        setIsSeeking(false);
        requestSeekToTime(progressBarTime);
        return undefined;
      }

      if (!mediaDuration) return;

      const boundingBox = event.target.getBoundingClientRect();

      const tapPositionX = event.center.x - boundingBox.left;
      const tapPercentageX = tapPositionX / boundingBox.width;

      const time = tapPercentageX * mediaDuration;

      return time;
    },
    [mediaDuration, progressBarTime, requestSeekToTime],
  );

  const localSeek = React.useCallback(
    (event: HammerInput) => {
      setIsSeeking(true);

      const time = calculateTime(event);

      if (time) {
        setProgressBarTime(time);
      }
    },
    [setProgressBarTime, calculateTime],
  );

  const seekAndTrack = React.useCallback(
    (event: HammerInput) => {
      const time = calculateTime(event);

      if (time) {
        setIsSeeking(false);
        requestSeekToTime(time);
      }
    },
    [requestSeekToTime, calculateTime],
  );

  return {
    progressBarTime,
    localSeek,
    seekAndTrack,
  };
};

const ProgressBar: React.FC = () => {
  const { mediaDuration } = usePlayer();
  const { progressBarTime, localSeek, seekAndTrack } = useSeek();

  const remainingTime = mediaDuration
    ? `-${secondsToMinutes(mediaDuration - progressBarTime)}`
    : '00:00';

  const progress = !mediaDuration ? 0 : (progressBarTime / mediaDuration) * 100;

  return (
    <>
      <Hammer onTap={seekAndTrack} onPan={localSeek} onPanEnd={seekAndTrack}>
        <div className="Player__ProgressBar__control"> </div>
      </Hammer>
      <div className="Player__ProgressBar__wrapper">
        <div
          style={{
            width: `${progress}%`,
          }}
          className="Player__ProgressBar"
        />
        <div
          className="Player__ProgressBarPosition__wrapper"
          style={{ marginLeft: `${progress}%` }}
        >
          <div className="Player__ProgressBarPosition" />
        </div>
      </div>
      <div className="Player__times">
        <Typography variant="h4">
          {secondsToMinutes(progressBarTime)}
        </Typography>
        <Typography variant="h4">{remainingTime}</Typography>
      </div>
    </>
  );
};

export default ProgressBar;
